import {
    AlertChild,
    AlertHistoryChild,
    HierarchyChild,
    IAlertResponse,
} from '@kcftech/prometheus-client';
import { getSystemWorkbenchUrl, WorkbenchTab } from 'utils/RouteHelper';

/**
 * Formats an alert timestamp.
 * @param formatDate - The function to format the date.
 * @param formatTime - The function to format the time.
 * @param date - The date to format.
 * @returns The formatted timestamp.
 */
export function formatAlertTimestamp(
    formatDate: (date: Date) => string,
    formatTime: (date: Date) => string,
    date: Date,
): string {
    return `Created ${formatDate(date)} at ${formatTime(date)}`;
}

/**
 * Returns the Workbench URL for an alert.
 * @param hierarchy - The hierarchy of the alert.
 * @param alertId - The ID of the alert.
 * @returns The Workbench URL for the alert.
 */
export function getWorkbenchUrl(
    hierarchy: Array<{ HierarchyId: string; HierarchyType: string } | undefined>,
    alertId: string,
): string {
    // System is always the first item in the hierarchy
    const systemId = hierarchy[0]?.HierarchyId;
    if (!systemId) {
        return '';
    }
    return getSystemWorkbenchUrl(systemId, WorkbenchTab.Trend, { alertId });
}

type AlertIndicatorInfo = {
    name: string;
    id: string;
    source: 'prometheus' | 'data-platform';
};

type AlertHierarchyIndicatorInfo = AlertIndicatorInfo & {
    hierarchy: HierarchyChild[];
};

/**
 * Returns the indicator extracted from the alert with hierarchy.
 * @param alert The alert to extract the indicator from.
 * @returns The extracted indicator.
 */
export function getIndicatorHierarchyFromAlert(
    alert: AlertChild | AlertHistoryChild,
): AlertHierarchyIndicatorInfo {
    const alertIndicatorInfo = getIndicatorFromAlert(alert);

    if (alert.Indicator) {
        return {
            ...alertIndicatorInfo,
            hierarchy: alert.Indicator.Hierarchy,
        };
    }

    if (alert.DataPlatformIndicator) {
        return {
            ...alertIndicatorInfo,
            hierarchy: alert.DataPlatformIndicator.Hierarchy,
        };
    }

    throw new Error(
        'Invalid Alert data: Both Indicator and DataPlatformIndicator are missing.',
    );
}

/**
 * Returns the indicator extracted from the alert without hierarchy.
 * @param alert The alert to extract the indicator name from.
 * @returns The extracted indicator name.
 */
export function getIndicatorFromAlert(
    alert: AlertChild | AlertHistoryChild | IAlertResponse,
): AlertIndicatorInfo {
    if (alert.Indicator) {
        return {
            name: alert.Indicator.IndicatorName,
            id: alert.Indicator.IndicatorId,
            source: 'prometheus',
        };
    }

    if (alert.DataPlatformIndicator) {
        return {
            name: alert.DataPlatformIndicator.DpIndicatorName,
            id: alert.DataPlatformIndicator.DpIndicatorId,
            source: 'data-platform',
        };
    }

    throw new Error(
        'Invalid Alert data: Both Indicator and DataPlatformIndicator are missing.',
    );
}
